import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Loader2, RefreshCw } from "lucide-react";

import { PunchMap } from "@/components/me/PunchMap";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import {
  geoErrorFor,
  getCurrentPosition,
  isGeoError,
  type GeoError,
  type GeoPosition,
} from "@/hooks/useGeolocation";
import { ApiError } from "@/lib/api";
import { formatTimeIn } from "@/lib/datetime";
import {
  employeeApi,
  type CheckInPreview,
  type EmployeeDay,
  type EmployeeSession,
  type EmployeeSite,
} from "@/lib/employee-api";
import { hasGoogleMapsKey } from "@/lib/google-maps";
import { checkInBody, checkOutBody, isReadingFresh } from "@/lib/punch";
import {
  accuracyLabel,
  checkInVerdict,
  describeLocation,
  type LocationSummary,
  type TodayAction,
} from "@/lib/today-status";
import { formatDuration } from "@/lib/work-hours";

export type PunchKind = "in" | "out";

const TONE: Record<LocationSummary["tone"], string> = {
  good: "border-success/30 text-success",
  warn: "border-warning/40 text-warning",
  info: "border-info/30 text-info",
  neutral: "border-border text-muted-foreground",
};

interface Reading {
  position: GeoPosition;
  /** The server's own verdict (BR-153); null when it could not be asked. */
  preview: CheckInPreview | null;
  /** `Date.now()` when taken — a punch never sends one older than 30 s. */
  at: number;
}

/**
 * The confirmation step of a web check-in or check-out (FEAT-2026-025) — the
 * counterpart of the app's check-in and check-out screens.
 *
 * It reads the location fresh, asks the server's web preview, and shows what
 * the server would do: where the employee is, how accurate the reading is,
 * and — before a check-in — whether it would be accepted. It applies **no
 * accuracy rule of its own**: the portal used to stop at any reading worse
 * than the organisation's limit and say it "may be rejected", where the app
 * and the server accept it whenever the GPS-only fallback is on (BR-164).
 *
 * Nothing is ever blocked here. A check-in the server would refuse is offered
 * as "Check in anyway" (BR-153) — the real punch still decides.
 */
export function PunchDialog({
  kind,
  action,
  tz,
  maxAccuracyM,
  site,
  openSession,
  shortfall,
  onClose,
  onPunched,
}: {
  /** Null while closed. */
  kind: PunchKind | null;
  /** Check in, or check in again — for the verdict's wording. */
  action: TodayAction;
  tz: string;
  maxAccuracyM: number;
  /** The site the punch is judged against, for the map. */
  site: EmployeeSite | null;
  /** For a check-out: the session being closed, judged against its own site. */
  openSession: EmployeeSession | null;
  /** How short today would close if checked out now (BR-155), or null. */
  shortfall: number | null;
  onClose: () => void;
  onPunched: (day: EmployeeDay, kind: PunchKind, position: GeoPosition) => void;
}) {
  const [reading, setReading] = useState<Reading | null>(null);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<GeoError | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [remarks, setRemarks] = useState("");

  const sessionSiteId = openSession?.siteId ?? null;

  /** Where the server should judge this punch from — a check-out, the session's own site. */
  const previewBody = useCallback(
    (position: GeoPosition) => ({
      ...checkInBody(position, navigator.userAgent),
      ...(kind === "out" && sessionSiteId ? { siteId: sessionSiteId } : {}),
    }),
    [kind, sessionSiteId],
  );

  const read = useCallback(async () => {
    setLocating(true);
    setError(null);
    setFailure(null);
    try {
      const position = await getCurrentPosition();
      let preview: CheckInPreview | null = null;
      try {
        preview = await employeeApi.previewCheckIn(previewBody(position));
      } catch {
        // Fail open, as the portal always has: a preview that could stop a
        // punch would be worse than none. The accuracy is still worth showing.
      }
      setReading({ position, preview, at: Date.now() });
    } catch (e) {
      setReading(null);
      setError(isGeoError(e) ? e : geoErrorFor("unavailable"));
    } finally {
      setLocating(false);
    }
  }, [previewBody]);

  // A fresh reading every time the confirmation opens; nothing carried over.
  useEffect(() => {
    if (!kind) return;
    setReading(null);
    setError(null);
    setFailure(null);
    setRemarks("");
    void read();
  }, [kind, read]);

  const confirm = async () => {
    if (!kind || !reading) return;
    setSubmitting(true);
    setError(null);
    setFailure(null);
    try {
      let position = reading.position;
      // A punch records where the employee is when it is sent. If the
      // confirmation sat open, read again — the app's rule too (BR-ATT-009).
      if (!isReadingFresh(reading.at, Date.now())) {
        try {
          position = await getCurrentPosition();
        } catch (e) {
          setError(isGeoError(e) ? e : geoErrorFor("unavailable"));
          return;
        }
      }
      const note = remarks.trim();
      // Built per punch, never shared: only a check-in takes `deviceInfo`, and
      // the API refuses a property it does not declare (see lib/punch.ts).
      const day =
        kind === "out"
          ? await employeeApi.checkOut({ ...checkOutBody(position), ...(note ? { remarks: note } : {}) })
          : await employeeApi.checkIn(checkInBody(position, navigator.userAgent));
      onPunched(day, kind, position);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const summary = reading?.preview ? describeLocation(reading.preview, kind === "out" ? "check-out" : "check-in") : null;
  const verdict = kind === "in" && reading?.preview ? checkInVerdict(reading.preview, action) : null;
  const coarse =
    reading != null &&
    (!Number.isFinite(reading.position.accuracy) || reading.position.accuracy > maxAccuracyM);
  const busy = locating || submitting;

  let body: ReactNode;
  if (!reading && (locating || !error)) {
    body = (
      <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Finding your location…
      </p>
    );
  } else if (!reading && error) {
    body = <p className="py-2 text-sm text-destructive">{error.message}</p>;
  } else if (reading) {
    body = (
      <div className="space-y-3">
        {hasGoogleMapsKey && <PunchMap site={site} position={reading.position} />}

        {summary ? (
          <div className="flex items-start gap-2">
            <Badge variant="outline" className={`shrink-0 whitespace-nowrap ${TONE[summary.tone]}`}>
              {summary.label}
            </Badge>
            <p className="text-sm">{summary.detail}</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Couldn't check this with the server just now — you can still{" "}
            {kind === "out" ? "check out" : "check in"}.
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 rounded-lg border p-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">GPS accuracy</p>
            <p className={coarse ? "font-medium text-warning" : "font-medium"}>
              <span>{accuracyLabel(reading.position.accuracy)}</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Checked</p>
            <p className="font-medium">{formatTimeIn(new Date(reading.at), tz)}</p>
          </div>
        </div>

        {verdict && (
          <p className={verdict.allowed ? "text-sm text-success" : "text-sm text-warning"}>
            {verdict.message}
          </p>
        )}

        {kind === "out" && shortfall != null && (
          <p className="text-sm text-warning">
            You're {formatDuration(shortfall)} short of your hours for today. Checking out now
            records a partial day.
          </p>
        )}

        {kind === "out" && (
          <Field label="Remarks (optional)">
            <Textarea
              rows={2}
              maxLength={500}
              value={remarks}
              placeholder="e.g. Wrapped up the site inspection report"
              onChange={(e) => setRemarks(e.target.value)}
            />
          </Field>
        )}

        {error && <p className="text-sm text-destructive">{error.message}</p>}
        {failure && <p className="text-sm text-destructive">{failure}</p>}
      </div>
    );
  }

  const primaryLabel =
    kind === "out"
      ? "Confirm check-out"
      : verdict && !verdict.allowed
        ? "Check in anyway"
        : "Confirm check-in";

  return (
    <Dialog open={kind != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{kind === "out" ? "Check out" : "Check in"}</DialogTitle>
          <DialogDescription>
            {site?.name ?? "Your work location"} — your location is checked with the server before
            anything is recorded.
          </DialogDescription>
        </DialogHeader>

        {body}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {(reading || error) && (
            <Button variant="outline" onClick={() => void read()} disabled={busy}>
              {locating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {reading ? "Check again" : "Try again"}
            </Button>
          )}
          {reading && (
            <Button onClick={() => void confirm()} disabled={busy}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {primaryLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
